
import React, { useState } from "react";
import GeocodeComponent from "./GeocodeComponent";
import MapComponent from "./MapComponent";
import buildingList from "../data/buildingList";
import { auth } from "../services/firebase";
import { formatDateToUS } from "../utils/dateFormatter";

/**
 * ScheduleMeetButton
 * Handles all meeting scheduling logic, including modal, validation, and sending notifications.
 * Props:
 *   recipientId: string (user id to send invite to)
 *   recipientName: string (for display)
 *   style: object (optional, for custom button styling)
 *   skipNotification: boolean (if true, just return data via onSubmit without sending notification)
 *   initialDate: string (pre-populate date field)
 *   initialTime: string (pre-populate time field)
 *   initialLocation: object (pre-populate location { address, lat, lng })
 *   buyerId: string (preserve buyer role)
 *   sellerId: string (preserve seller role)
 *   listingId: string (associated listing)
 */

function ScheduleMeetButton({ recipientId, style, open: forceOpen, onCancel, onSubmit, skipNotification = false, initialDate = "", initialTime = "", initialLocation = null, buyerId = null, sellerId = null, listingId = null }) {
  const [open, setOpen] = useState(!!forceOpen);
  const [date, setDate] = useState(initialDate);
  const [time, setTime] = useState(initialTime);
  const [error, setError] = useState("");
  const [selectedLocation, setSelectedLocation] = useState(initialLocation); // { address, lat, lng }
  const [mapCenter, setMapCenter] = useState(
    initialLocation && initialLocation.lat && initialLocation.lng 
      ? { lat: initialLocation.lat, lng: initialLocation.lng }
      : { lat: 40.508, lng: -88.991 }
  );
  const [selectedBuilding, setSelectedBuilding] = useState("");
  const user = auth.currentUser;

  const handleSubmit = async () => {
    if (!date || !time) {
      setError("Please select a date and time.");
      return;
    }
    if (!selectedLocation) {
      setError("Please select a meeting location (search or click on the map).");
      return;
    }
    
    // Skip recipient validation if skipNotification is true (for suggesting new time)
    if (!skipNotification) {
      if (!recipientId || recipientId === "yes" || recipientId.length < 10) {
        alert("Invalid recipient information. Unable to schedule meeting.");
        return;
      }
      if (recipientId === user?.uid) {
        alert("You cannot schedule a meeting with yourself.");
        return;
      }
    }
    
    setError("");
    setOpen(false);
    
    const { address, lat, lng } = selectedLocation;
    
    // If skipNotification is true, just call onSubmit and return (for suggesting new time)
    if (skipNotification) {
      if (onSubmit) {
        onSubmit({ date, time, address, lat, lng });
      }
      // Reset form
      setDate("");
      setTime("");
      setSelectedLocation(null);
      setMapCenter({ lat: 40.508, lng: -88.991 });
      return;
    }
    
    // Keep date in YYYY-MM-DD (ISO) format for database storage
    // Only convert to MM-DD-YYYY for display in messages
    const displayDate = formatDateToUS(date);
    
    // Otherwise, send the notification as usual
    const message = `Meet scheduled for ${displayDate} at ${time} at ${address}`;
    await import("../utils/notifications").then(({ sendInviteNotification }) =>
      sendInviteNotification({
      recipientId,
      senderId: user.uid,
      date: date,  // Store in YYYY-MM-DD format for calendar filtering
      time,
      message,
      location: { address, lat, lng },
      buyerId: buyerId || user.uid,  // Sender is buyer if not specified
      sellerId: sellerId || recipientId,  // Recipient is seller if not specified
      listingId
      })
    );
    alert(`Meeting invite sent successfully for ${displayDate} at ${time} at ${address}.`);
    setDate("");
    setTime("");
    setSelectedLocation(null);
    setMapCenter({ lat: 40.508, lng: -88.991 });
    
    // Call parent's onSubmit if provided
    if (onSubmit) {
      onSubmit({ date, time, address, lat, lng });
    }
  };

  return (
    <>
      {!forceOpen && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          style={{
            background: '#d50032',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '7px 18px',
            fontWeight: 600,
            fontSize: 15,
            boxShadow: '0 2px 8px rgba(213,0,50,0.08)',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            transform: 'translateY(0)',
            ...style,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = '#b71c1c';
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(213,0,50,0.3)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = '#d50032';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(213,0,50,0.08)';
          }}
        >
          Schedule Meet
        </button>
      )}
      {open && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.18)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: 32, minWidth: 700, minHeight: 540, boxShadow: '0 4px 32px rgba(0,0,0,0.13)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18, boxSizing: 'border-box'
          }}>
            <h3 style={{ color: '#d50032', marginBottom: 8 }}>
              {skipNotification ? "Suggest New Time" : "Schedule Meet"}
            </h3>
            <input
              type="date"
              value={date}
              onChange={e => setDate(e.target.value)}
              style={{ padding: 8, borderRadius: 6, border: '1.5px solid #d50032', fontSize: 16, width: '100%' }}
            />
            <input
              type="time"
              value={time}
              onChange={e => setTime(e.target.value)}
              style={{ padding: 8, borderRadius: 6, border: '1.5px solid #d50032', fontSize: 16, width: '100%' }}
            />
            {/* Location selection UI inside modal */}
            <div style={{ display: 'flex', gap: 24, marginTop: 8, alignItems: 'stretch', minHeight: 260 }}>
              <div style={{ flex: 1 }}>
                {/* Building dropdown */}
                <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Select a building:</label>
                <select
                  value={selectedBuilding}
                  onChange={e => {
                    const val = e.target.value;
                    setSelectedBuilding(val);
                    if (val) {
                      const b = buildingList.find(b => b.name === val);
                      if (b) {
                        setSelectedLocation({ lat: b.lat, lng: b.lng, address: b.name });
                        setMapCenter({ lat: b.lat, lng: b.lng });
                      }
                    }
                  }}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1.5px solid #d50032', fontSize: 15, marginBottom: 10 }}
                >
                  <option value="">-- Select a building --</option>
                  {buildingList.map(b => (
                    <option key={b.name} value={b.name}>{b.name}</option>
                  ))}
                </select>
                <div style={{ margin: '10px 0', fontWeight: 500 }}>Or search for a place:</div>
                <GeocodeComponent
                  onResult={({ lat, lng, address }) => {
                    setSelectedLocation({ lat, lng, address });
                    setMapCenter({ lat, lng });
                    setSelectedBuilding("");
                  }}
                />
                {selectedLocation && (
                  <div style={{ marginTop: 8, fontSize: 15, color: '#333' }}>
                    <b>Selected:</b> {selectedLocation.address} <br />
                    <span style={{ fontSize: 13, color: '#888' }}>({selectedLocation.lat}, {selectedLocation.lng})</span>
                  </div>
                )}
              </div>
              <div style={{ flex: 2, minWidth: 220, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ flex: 1, display: 'flex' }}>
                  <MapComponent
                    center={mapCenter}
                    marker={selectedLocation ? { lat: selectedLocation.lat, lng: selectedLocation.lng } : mapCenter}
                    width={260}
                    height={260}
                    zoom={15}
                    onClick={e => {
                      if (e && e.latLng) {
                        const lat = e.latLng.lat();
                        const lng = e.latLng.lng();
                        setSelectedLocation({ lat, lng, address: `Lat: ${lat}, Lng: ${lng}` });
                        setMapCenter({ lat, lng });
                        setSelectedBuilding("");
                      }
                    }}
                  />
                </div>
                <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>Click on the map to drop a pin.</div>
              </div>
            </div>
            {error && <div style={{ color: '#d50032', fontSize: 15 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
              <button
                type="button"
                onClick={handleSubmit}
                style={{
                  background: '#d50032', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 18px', fontWeight: 600,
                  fontSize: 15, cursor: 'pointer'
                }}
              >
                {skipNotification ? "Suggest New Time" : "Schedule Meet"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  if (onCancel) onCancel();
                }}
                style={{ background: '#fff', color: '#d50032', border: '1.5px solid #d50032', borderRadius: 6, padding: '7px 18px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ScheduleMeetButton;
